import { memo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTypography from "sipli-fleet-core/components/CustomSharedTypography/CustomSharedTypography";
import CustomButton from "sipli-fleet-core/components/CustomSharedButton/CustomSharedButton";
import type { CustomButtonProps } from "sipli-fleet-core/components/CustomSharedButton/CustomSharedButton";

const { CustomTitle } = CustomTypography;

export interface CustomTitleSectionProps {
  titleKey: string;
  level?: 1 | 2 | 3 | 4 | 5;
  classNameContainer?: string;
  titleClassName?: string;
  buttonKey?: string;
  onClick?: () => void;
  to?: string;
  buttonType?: CustomButtonProps["type"];
  buttonSize?: CustomButtonProps["buttonSize"];
  danger?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  buttonProps?: Partial<CustomButtonProps>;
  showBtn?: boolean;
  rightBtnComponent?: ReactNode;
  backIcon?: ReactNode;
  subTitle?: ReactNode;
}

function CustomTitleSection({
  titleKey,
  level = 2,
  classNameContainer = "",
  titleClassName = "",
  buttonKey,
  onClick,
  to,
  buttonType = "primary",
  buttonSize = "medium",
  danger = false,
  disabled = false,
  icon,
  buttonProps = {},
  showBtn = true,
  rightBtnComponent,
  backIcon,
  subTitle = "",
}: CustomTitleSectionProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleButtonClick = () => {
    if (typeof onClick === "function") {
      onClick();
      return;
    }
    if (to) {
      navigate(to);
    }
  };

  const hasButton = (!!buttonKey || !!onClick || !!to) && showBtn;

  return (
    <div
      className={`flex items-center justify-between gap-4 w-full py-6 ${classNameContainer}`}
    >
      <div className="flex-1 min-w-0">
        <CustomTitle
          level={level}
          className={`!m-0 font-bold ${titleClassName}`}
        >
          {backIcon} {t(titleKey)}
        </CustomTitle>
        {subTitle && typeof subTitle === "string" ? (
          <CustomTypography.CustomText className="!text-gray9 !font-semibold !text-[15.75px] !ml-11">
            {subTitle}
          </CustomTypography.CustomText>
        ) : (
          subTitle && <div className="mt-2">{subTitle}</div>
        )}
      </div>

      <div className="flex-shrink-0">
        {hasButton && (
          <CustomButton
            type={buttonType}
            onClick={handleButtonClick}
            icon={icon}
            buttonSize={buttonSize}
            danger={danger}
            disabled={disabled}
            {...buttonProps}
            label={t(buttonKey ?? "")}
          />
        )}
        {rightBtnComponent}
      </div>
    </div>
  );
}

export default memo(CustomTitleSection);
